/**
 * AdvisorAuthContext — zentrale, EINMALIGE Auth-Prüfung fürs SB-Portal.
 *
 * Vorher prüften AdvisorLogin und AdvisorDashboard unabhängig voneinander
 * `/advisor/auth/me` und navigierten per useEffect gegenseitig aufeinander
 * (Login → Dashboard, Dashboard → Login). Bei jedem Seitenwechsel lief der
 * Check erneut → sichtbares Hin-und-Her-Flackern (Login ↔ Dashboard).
 *
 * Jetzt: ein Provider umschließt alle SB-Routen, führt den /me-Check genau
 * EINMAL aus und stellt den Zustand allen Seiten bereit. Weiterleitungen
 * erfolgen deklarativ über <Navigate> auf Basis des FERTIGEN Zustands —
 * dadurch keine Oszillation mehr.
 *
 * Cookie-Auth (T1): Token liegt im httpOnly 'advisor_access_token'-Cookie.
 * 'advisor_authed' (localStorage) ist nur ein nicht-sensibler Routing-Hinweis.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AdvisorAuthCtx = createContext(null);

export function AdvisorAuthProvider({ children }) {
  const [advisor, setAdvisor] = useState(null);
  // Loading nur, wenn ein Auth-Flag existiert (dann wird /me geprüft).
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('advisor_authed')));

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('advisor_authed')) {
      setAdvisor(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/advisor/auth/me`);
      setAdvisor(res.data);
    } catch {
      localStorage.removeItem('advisor_authed');
      setAdvisor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback((data) => {
    localStorage.setItem('advisor_authed', '1');
    setAdvisor(data);
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('advisor_authed');
    setAdvisor(null);
    setLoading(false);
  }, []);

  return (
    <AdvisorAuthCtx.Provider value={{ advisor, loading, login, logout, refresh }}>
      {children}
    </AdvisorAuthCtx.Provider>
  );
}

export function useAdvisorAuth() {
  const ctx = useContext(AdvisorAuthCtx);
  if (!ctx) throw new Error('useAdvisorAuth muss innerhalb von AdvisorAuthProvider verwendet werden');
  return ctx;
}
